import React, { useState, useEffect } from "react";
import {
  Button,
  Form,
  Spinner,
  Modal,
  Row,
  Col,
} from "react-bootstrap";
import { patchRequest, deleteRequest } from "services/api";
import { URLS } from "utils/constants";
import ServiceForm from "./ServiceForm";
import VehicleInfoCard from "./VehicleInfoCard";
import MaintenanceHistoryCard from "./MaintenanceHistoryCard";
import { IconArrowLeft } from "@tabler/icons-react";
import LayoutDashboard from "layouts/layout-dashboard-collapse";
import { useATV } from "../context/ATVContext";
import { useATVStatus } from "../context/ATVStatusContext";
import { useServices } from "../context/ServiceContext";
import { getStatusConfig } from "../utils/statusUtils";
import PrintJson from "utils/printJson";
import { useNavigate, useParams } from "react-router-dom";
import Swal from "sweetalert2";
import "./WorkshopTopbar.css";

export default function VehicleDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const { atvs, updateATV } = useATV();
  const { atvStatuses } = useATVStatus();
  const { services, createService, fetchServicesByATV } = useServices();

  const [editingService, setEditingService] = useState(null);

  // Find vehicle by ID from context
  const vehicle = atvs.data.find((atv) => String(atv.id) === String(id));

  const [vehicleStatus, setVehicleStatus] = useState(vehicle?.status_id || "");
  const [updatingStatus, setUpdatingStatus] = useState(false);
  const [showErrorDialog, setShowErrorDialog] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Update local state when vehicle data changes
  useEffect(() => {
    if (vehicle) {
      setVehicleStatus(vehicle.status_id);
    }
  }, [vehicle]);

  // Fetch services for this ATV
  useEffect(() => {
    if (vehicle?.id) {
      fetchServicesByATV(vehicle.id);
    }
  }, [vehicle?.id, fetchServicesByATV]);

  const handleStatusChange = async (newStatusId) => {
    if (!vehicle || !newStatusId || newStatusId === "") return;

    setUpdatingStatus(true);
    try {
      const result = await updateATV(vehicle.id, {
        status_id: newStatusId,
        plate_number: vehicle.plate_number,
      });
      if (result.success) {
        setVehicleStatus(newStatusId);
      } else {
        setErrorMessage(result.error || "Failed to update vehicle status");
        setShowErrorDialog(true);
      }
    } catch (error) {
      console.error("Error updating status:", error);
      setErrorMessage("Failed to update vehicle status. Please try again.");
      setShowErrorDialog(true);
    } finally {
      setUpdatingStatus(false);
    }
  };

  const handleDeleteService = async (service) => {
    const result = await Swal.fire({
      title: "Konfirmasi Hapus",
      text: `Apakah Anda yakin ingin menghapus service "${service.description}"?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonColor: "#d33",
      cancelButtonColor: "#3085d6",
      confirmButtonText: "Ya, Hapus!",
      cancelButtonText: "Batal",
    });

    if (result.isConfirmed) {
      try {
        const response = await deleteRequest(`/service/${service.id}`);
        if (response.success) {
          Swal.fire({
            icon: "success",
            title: "Berhasil",
            text: "Service berhasil dihapus!",
            confirmButtonText: "OK",
          });
          fetchServicesByATV(vehicle.id); // Refresh services
        } else {
          throw new Error(response.message || "Failed to delete service");
        }
      } catch (error) {
        console.error("Error deleting service:", error);
        setErrorMessage("Failed to delete service. Please try again.");
        setShowErrorDialog(true);
      }
    }
  };

  const handleSubmit = async (serviceData, serviceId) => {
    try {
      if (serviceId) {
        // Update service
        const response = await patchRequest(
          `/service/${serviceId}`,
          serviceData
        );
        if (!response.success) {
          throw new Error(response.message || "Failed to update service");
        }
        // alert("Service berhasil diupdate!");
        Swal.fire({
          icon: "success",
          title: "Berhasil",
          text: "Service berhasil diupdate!",
          confirmButtonText: "OK",
        });
      } else {
        await createService({
          ...serviceData,
          atv_id: vehicle.id,
        });
        // alert("Service berhasil disimpan!");
        Swal.fire({
          icon: "success",
          title: "Berhasil",
          text: "Service berhasil disimpan!",
          confirmButtonText: "OK",
        });
      }
      setOpen(false);
      setEditingService(null);
      fetchServicesByATV(vehicle.id); // Refresh services
    } catch (error) {
      console.error("Error saving service:", error);
      let errorMessage = "Failed to save service. Please try again.";
      if (
        error.response &&
        error.response.data &&
        error.response.data.message
      ) {
        errorMessage = error.response.data.message;
      } else if (
        error.response &&
        error.response.data &&
        error.response.data.error
      ) {
        errorMessage = error.response.data.error;
      } else if (error.error) {
        errorMessage = error.error;
      } else if (error.message) {
        errorMessage = error.message;
      }
      setErrorMessage(errorMessage);
      setShowErrorDialog(true);
    }
  };

  if (!vehicle) {
    return (
      <LayoutDashboard>
        <div className="container-fluid mt-5">
          <div className="text-center py-5">
            {atvs.loading ? (
              <div>
                <Spinner animation="border" className="mb-3" />
                <p className="text-muted">Loading vehicle...</p>
              </div>
            ) : (
              <>
                <h1 className="text-danger mb-4">Kendaraan tidak ditemukan</h1>
                <Button
                  onClick={() => navigate(URLS.v1.WORKSHOP)}
                  className="btn-primary border-0"
                >
                  Kembali ke Workshop
                </Button>
              </>
            )}
          </div>
        </div>
      </LayoutDashboard>
    );
  }

  const statusConfig = getStatusConfig(vehicleStatus, atvStatuses.data);

  return (
    <LayoutDashboard>
      <div className="container-fluid mt-5 workshop-page-scope">
        {/* Header */}
        <div className="border-bottom border-light pb-3 mb-4">
          <div className="d-flex align-items-center gap-3 mb-3 flex-column flex-md-row">
            <Button
              variant="outline-secondary"
              size="sm"
              onClick={() => navigate(URLS.v1.WORKSHOP)}
              className="border"
            >
              <IconArrowLeft size={16} />
            </Button>
            <div className="flex-grow-1 text-center text-md-start">
              <h1 className="h2 fw-bold mb-1 text-dark">{vehicle.name}</h1>
              <p className="text-muted mb-0 fs-5">{vehicle.plate_number}</p>
            </div>
            <Form.Select
              value={vehicleStatus}
              onChange={(e) => handleStatusChange(e.target.value)}
              disabled={updatingStatus}
              className="w-auto fw-medium border-2"
              style={{
                backgroundColor: statusConfig.bgColor,
                color: statusConfig.color,
                borderColor: statusConfig.color,
              }}
            >
              {atvStatuses.data.map((status) => (
                <option key={status.id} value={status.id}>
                  {status.name}
                </option>
              ))}
            </Form.Select>
          </div>
        </div>

        <div className="container-fluid">
          <Row className="g-4">
            {/* Left Column - Vehicle Info */}
            <Col md={12} lg={4}>
              <VehicleInfoCard
                vehicle={vehicle}
                vehicleStatus={vehicleStatus}
                atvStatuses={atvStatuses}
                updatingStatus={updatingStatus}
                onStatusChange={handleStatusChange}
                onAddService={() => setOpen(true)}
              />
            </Col>

            {/* Right Column - Maintenance History */}
            <Col md={12} lg={8}>
              <MaintenanceHistoryCard
                services={services}
                onAddService={() => setOpen(true)}
                onEditService={(service) => {
                  setEditingService(service);
                  setOpen(true);
                }}
                onDeleteService={handleDeleteService}
              />
            </Col>
          </Row>
        </div>

        {/* Service Form Modal */}
        <ServiceForm
          open={open}
          onOpenChange={(isOpen) => {
            setOpen(isOpen);
            if (!isOpen) setEditingService(null);
          }}
          onSubmit={handleSubmit}
          atvId={vehicle?.id}
          serviceId={editingService?.id}
        />

        {/* Error Dialog */}
        <Modal
          show={showErrorDialog}
          onHide={() => setShowErrorDialog(false)}
          centered
          className="modal-blur"
        >
          <Modal.Header closeButton className="modal-header">
            <Modal.Title className="modal-title">
              <svg
                className="icon icon-tabler icon-tabler-alert-triangle me-2"
                width="24"
                height="24"
                viewBox="0 0 24 24"
                strokeWidth="2"
                stroke="currentColor"
                fill="none"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                <path d="M12 9v2m0 4v.01"></path>
                <path d="M5 19h14a2 2 0 0 0 1.84 -2.75l-7.1 -12.25a2 2 0 0 0 -3.5 0l-7.1 12.25a2 2 0 0 0 1.75 2.75"></path>
              </svg>
              Error
            </Modal.Title>
          </Modal.Header>
          <Modal.Body className="modal-body">
            <div className="text-center">
              <div className="mb-3">
                <svg
                  className="icon icon-tabler icon-tabler-alert-circle text-danger"
                  width="48"
                  height="48"
                  viewBox="0 0 24 24"
                  strokeWidth="1.5"
                  stroke="currentColor"
                  fill="none"
                  strokeLinecap="round"
                  strokeLinejoin="round"
                >
                  <path stroke="none" d="M0 0h24v24H0z" fill="none"></path>
                  <circle cx="12" cy="12" r="9"></circle>
                  <line x1="12" y1="8" x2="12" y2="12"></line>
                  <line x1="12" y1="16" x2="12.01" y2="16"></line>
                </svg>
              </div>
              <h5 className="modal-title mb-3">Operation Failed</h5>
              <p className="text-muted mb-0">{errorMessage}</p>
            </div>
          </Modal.Body>
          <Modal.Footer className="modal-footer">
            <button
              type="button"
              className="btn btn-primary"
              onClick={() => setShowErrorDialog(false)}
            >
              OK
            </button>
          </Modal.Footer>
        </Modal>
      </div>
      <PrintJson data={vehicle} name="Vehicle Detail" />
      <PrintJson data={services} name="Vehicle Detail" />
    </LayoutDashboard>
  );
}
